import { useState, Fragment, useContext, useEffect } from 'react';
import { useGet } from '@/query/fetchHooks';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/DataTable/DataTable';
import { format } from 'date-fns';
import { config } from '@/config';
import { T } from '@/components/T';
import { Button, InputText } from '@teo/components';
import { Search, Check } from '@teo/components/icons';
import { newUid, useMobile } from '@/util';
import TagModal from './TagModal';
import OptionsAction from '@/components/DataTable/OptionsAction';
import i18n from 'i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';
import { NavContext } from '@/components/NavContext';

function ManageTagsPage() {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);
  const [isOpenTagModal, setIsOpenTagModal] = useState(false);
  const [isAddModal, setIsAddModal] = useState<any>(true);
  const [refetchTable, setRefetchTable] = useState<string>(newUid(20));
  const [activeTags, setActiveTags] = useState<any>(null);
  const [activeTag, setActiveTag] = useState<any>(null);
  const [isTagIndex, setIsTagIndex] = useState<string>('');

  const [selected, setSelected] = useState<Set<string | number>>(
    new Set<string | number>()
  );
  const [deselect, setDeselect] = useState<boolean>(false);
  const { auth } = useContext(AuthContext);
  const { setSubPage } = useContext(NavContext);

  const { refetch: refetchActiveTags } = useGet('/tags', {
    primary: true,
    lang: i18n.language,
    onSuccess: (result: any) => {
      setActiveTags(result?.data || {});
    },
  });

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  const handleEdit = (id: any) => {
    setIsOpenTagModal(true);
    setIsAddModal(false);
    setIsTagIndex(id);
  };

  return (
    <div className="max-w-6xl pb-8">
      {isMobile && <MobileSubPageMenu title={t('pages.manage_tags.title')} />}
      <div className="flex w-full items-center justify-between">
        {!isMobile && (
          <h1 className="my-8 text-xl font-semibold text-black">
            <T>{t('pages.manage_tags.title')}</T>
          </h1>
        )}

        <div className="mb-6 ml-auto flex items-center">
          <Button
            data-testid="addTagModal"
            onClick={() => {
              setIsOpenTagModal(true);
              setIsAddModal(true);
            }}
            size="md"
          >
            {t('pages.manage_tags.add_tag_button')}
          </Button>
          {/* <div className="pl-2">
            <IconButton
              Icon={MoreVertical}
              variant="ghost"
              className="text-[#919193]"
            />
          </div> */}
        </div>
      </div>
      <Fragment>
        <div className="pb-8 sm:w-2/3 md:w-1/2" data-testid="tagsTable">
          <InputText
            value={filter}
            onChange={(ev) => setFilter(ev.target.value)}
            Icon={Search}
            placeholder={t('search.course')}
            data-testid="searchTag"
          />
        </div>
        {activeTags?.length > 0 && (
          <div className="mb-4 flex items-center gap-3">
            <span className="text-sm font-medium">
              {t('pages.manage_tags.primary_tags')}:
            </span>
            {activeTags?.map((tag: any) => {
              return (
                <div
                  className="text-sm-medium rounded-md bg-grey-02 px-2 py-1"
                  key={tag?.id}
                >
                  {tag?.tag}
                </div>
              );
            })}
          </div>
        )}
        <DataTable
          filter={debouncedFilter}
          fetch={useGet.bind(
            null,
            `/tags?lang=${i18n.language}&organisationId=${auth?.user?.activeOrganisationId},OR,organisationId=null`
          )}
          empty={{
            template: 1,
            text: t('empty_tenplate.tag_list'),
            btn_text: t('pages.manage_tags.add_tag_button'),
            btnClick: () => {
              setIsOpenTagModal(true);
              setIsAddModal(true);
            },
          }}
          key={refetchTable}
          screensUpdateTable="mobile"
          selectable={{
            selected: selected,
            setSelected: setSelected,
            deselect: deselect,
            setDeselect: setDeselect,
          }}
          defaultSort={[
            { id: 'tag', desc: false },
            { id: 'createdAt', desc: false },
          ]}
          columns={[
            {
              title: 'Tag',
              id: 'tag',
              className: 'col-start-2 col-end-9  row-start-1',
              render: (tag: any) => (
                <div className="flex items-center text-sm font-medium">
                  {tag.tag}
                </div>
              ),
            },
            {
              addTitleOnMobile: true,
              id: 'createdAt',
              title: t('columns.date_created'),
              align: 'right',
              className:
                'col-start-1 col-end-10 row-start-2 justify-end text-right',
              render: (tag: any) =>
                format(new Date(tag.createdAt), 'dd/MM/yyyy'),
            },
            {
              addTitleOnMobile: true,
              id: 'primary',
              title: t('pages.manage_tags.primary_tags'),
              align: 'right',
              className:
                'col-start-1 col-end-10 row-start-2 justify-end text-right',
              render: (tag: any) => (
                <div className="flex justify-end">
                  {tag?.primary ? (
                    <Check className="h-6 w-6 text-success-05" />
                  ) : (
                    ''
                  )}
                </div>
              ),
            },
            {
              title: '',
              id: 'edit',
              sortable: false,
              className: 'col-start-9 row-start-1 justify-end',
              render: (tag: any) => (
                <div className="flex justify-end">
                  {tag.organisationId !== null && (
                    <OptionsAction
                      delete={{
                        title: t('pages.manage_tags.delete_tag'),
                        request: `${config.backend}/tag/${tag.id}?organisationId=${auth?.user?.activeOrganisationId}`,
                        selected: selected,
                        setSelected: setSelected,
                      }}
                      edit={{
                        title: t('pages.manage_tags.edit_tag'),
                        request: () => {
                          handleEdit(tag.id);
                          setActiveTag(tag);
                        },
                      }}
                      rowInfo={tag}
                      screensUpdateTable="mobile"
                      setRefetchTable={setRefetchTable}
                      mobileHeader={{
                        render: (tag: any) => (
                          <div className="flex items-center text-sm font-medium">
                            {tag.tag}
                          </div>
                        ),
                      }}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Fragment>
      {isOpenTagModal && (
        <TagModal
          setIsOpenTagModal={setIsOpenTagModal}
          isOpenTagModal={isOpenTagModal}
          closeModal={() => setIsOpenTagModal(false)}
          onChange={() => {
            setRefetchTable(newUid(20));
            refetchActiveTags();
          }}
          isAddModal={isAddModal}
          isTagIndex={isTagIndex}
          activeTag={activeTag}
          activeTags={activeTags}
        />
      )}
    </div>
  );
}

export default ManageTagsPage;
